import { FC, useMemo } from "react";
import moment from "moment";
import { Download01 } from "untitledui-js-base";
import { Pagination } from "shared/components/Pagination";
import { Table } from "shared/app/Table";
import { IconButton, IconButtonSize } from "shared/components";
import { Badge, BadgeSize, BadgeVariant } from "shared/components/Badge";

import { useReactTable, getCoreRowModel } from "@tanstack/react-table";

import { cx } from "utils/classnames";
import { useNavigate } from "react-router-dom";
import { Statement, StatementStatus } from "../types";
import FeeDisplay from "shared/components/FeeDisplay";

interface BillingTableProps {
  data: Statement[];
  pageCount: number;
  currentPage: number;
  isLoading: boolean;
  getCSVLineItems: (
    invoiceId: string,
    payoutId: string,
    search: string
  ) => Promise<any[]>;
  setPage: (page: number | ((prevPage: number) => number)) => void;
  setSelectedStatementId: (id: number | null) => void;
}

export const BillingTable: FC<BillingTableProps> = ({
  data,
  pageCount,
  currentPage,
  setPage,
  setSelectedStatementId,
  isLoading
}) => {
  const navigate = useNavigate();

  const onDownloadIconClick = async (_, item) => {
    setSelectedStatementId(item.id);
  };

  const columns = useMemo(() => {
    return [
      {
        id: "id fixed_width",
        accessorKey: "id",
        size: 143,
        header: "Statement ID"
      },
      {
        accessorKey: "period",
        header: "Month",
        cell: (info) =>
          info.getValue().start ? (
            <p className="font-medium">
              {moment.utc(info.getValue().start).format("MMMM YYYY")}
            </p>
          ) : (
            "N/A"
          )
      },
      {
        id: "created_at fixed_width",
        accessorKey: "due_date",
        size: 116,
        header: "Due date",
        cell: (info) =>
          moment.utc(info.getValue()).isValid()
            ? moment.utc(info.getValue()).format("MMM DD, YYYY")
            : "-"
      },
      {
        id: "total custom_ right-align fixed_width",
        accessorKey: "total",
        header: "Statement total",
        size: 157,
        cell: (info) => {
          const total = info.getValue();

          return (
            <FeeDisplay
              feeInCents={total.cents}
              currency="USD"
              className={cx("font-medium text-end mr-4")}
            />
          );
        }
      },
      {
        id: "status fixed_width right-align custom_",
        accessorKey: "status",
        header: "Status",
        size: 113,
        cell: (info) => (
          <div className="text-end mr-4">
            <Badge
              size={BadgeSize.SM}
              className="shrink-0"
              variant={
                info.getValue() === StatementStatus.Finished
                  ? BadgeVariant.SUCCESS
                  : BadgeVariant.PRIMARY
              }
            >
              {info.getValue() === StatementStatus.Finished ? "Paid" : "Unpaid"}
            </Badge>
          </div>
        )
      },
      {
        id: "action fixed_width",
        header: "",
        size: 114,
        cell: (p) => {
          const item = p.row.original;
          return (
            <div className="flex items-center justify-center gap-6">
              <p className="text-xs font-bold text-content-300">View</p>
              <IconButton
                size={IconButtonSize.SM}
                Icon={Download01}
                className="stroke-content-200"
                borderLess
                onClick={(e) => {
                  e.stopPropagation();
                  onDownloadIconClick(e, item);
                }}
              />
            </div>
          );
        }
      }
    ];
  }, []);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <>
      <Table
        table={table}
        onRowClick={(id) => navigate(`/settings/statements/${id}`)}
        isLoading={isLoading}
      />
      <div className="flex mt-4">
        <Pagination
          className="justify-end"
          total={pageCount}
          current={currentPage}
          onChange={(p) => setPage(p)}
        />
      </div>
    </>
  );
};
